// Two-month range calendar: first click sets start, second sets end (or restarts if earlier).
// Opción single_date: una sola fecha (target singleInput); con auto_submit_on_complete, al elegir día envía el formulario.
// Syncs ISO dates (YYYY-MM-DD) a inputs ocultos (start/end o singleInput).
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["startInput", "endInput", "singleInput", "leftMonth", "rightMonth", "leftTitle", "rightTitle", "panel", "trigger", "displayLabel"]
  static values = {
    translations: { type: Object, default: { days: [], months: [] } },
    holidayLabels: { type: Object, default: {} },
    singleDate: { type: Boolean, default: false },
    autoSubmitOnComplete: { type: Boolean, default: false },
    requireApplyButton: { type: Boolean, default: false }
  }

  connect() {
    if (this.singleDateValue && this.hasSingleInputTarget) {
      const v = this.normalizeIso(this.singleInputTarget.value)
      this.rangeStart = v
      this.rangeEnd = v
    } else if (this.hasStartInputTarget && this.hasEndInputTarget) {
      this.rangeStart = this.normalizeIso(this.startInputTarget.value) || null
      this.rangeEnd = this.normalizeIso(this.endInputTarget.value) || null
    }
    this.viewMonth = this.firstOfMonth(this.parseIso(this.rangeStart) || new Date())
    this.boundEnsureRange = this.ensureRangeCompleteBeforeSubmit.bind(this)
    this.formElement = this.element.closest("form")
    if (this.formElement && !this.singleDateValue) {
      this.formElement.addEventListener("submit", this.boundEnsureRange)
    }
    if (this.hasPanelTarget) {
      this.open = false
      this.panelTarget.classList.add("hidden")
      this.boundOutside = this.onDocumentPointerDown.bind(this)
      document.addEventListener("pointerdown", this.boundOutside, true)
      this.boundEscape = this.onEscape.bind(this)
      document.addEventListener("keydown", this.boundEscape)
    }
    this.updateDisplayLabel()
    this.render()
  }

  disconnect() {
    if (this.formElement && this.boundEnsureRange && !this.singleDateValue) {
      this.formElement.removeEventListener("submit", this.boundEnsureRange)
    }
    if (this.boundOutside) {
      document.removeEventListener("pointerdown", this.boundOutside, true)
    }
    if (this.boundEscape) {
      document.removeEventListener("keydown", this.boundEscape)
    }
  }

  onEscape(event) {
    if (event.key !== "Escape" || !this.open) return
    this.closePanel()
  }

  onDocumentPointerDown(event) {
    if (!this.open || !this.hasPanelTarget) return
    if (this.element.contains(event.target)) return
    this.closePanel()
  }

  togglePanel(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.open) {
      this.closePanel()
    } else {
      this.openPanel()
    }
  }

  openPanel() {
    if (!this.hasPanelTarget) return
    if (this.rangeStart) {
      const d = this.parseIso(this.rangeStart)
      if (d) this.viewMonth = this.firstOfMonth(d)
    }
    this.panelTarget.classList.remove("hidden")
    this.open = true
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", "true")
    }
    this.render()
  }

  closePanel() {
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.add("hidden")
    this.open = false
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", "false")
    }
  }

  previousMonth() {
    this.viewMonth.setMonth(this.viewMonth.getMonth() - 1)
    this.render()
  }

  nextMonth() {
    this.viewMonth.setMonth(this.viewMonth.getMonth() + 1)
    this.render()
  }

  pickDay(event) {
    event.stopPropagation()
    const iso = event.currentTarget.dataset.iso
    if (!iso) return

    if (this.singleDateValue && this.hasSingleInputTarget) {
      this.rangeStart = iso
      this.rangeEnd = iso
      this.syncInputs()
      this.render()
      if (this.hasPanelTarget) {
        this.closePanel()
      }
      if (this.autoSubmitOnCompleteValue) {
        this.submitForm()
      }
      return
    }

    const pickingSecondEdge = Boolean(this.rangeStart && !this.rangeEnd)

    if (!this.rangeStart || (this.rangeStart && this.rangeEnd)) {
      this.rangeStart = iso
      this.rangeEnd = null
    } else if (pickingSecondEdge && iso === this.rangeStart) {
      // Un solo día: segundo clic en la misma fecha (desde = hasta).
      this.rangeEnd = iso
    } else if (iso < this.rangeStart) {
      this.rangeStart = iso
      this.rangeEnd = null
    } else {
      this.rangeEnd = iso
    }

    this.syncInputs()
    this.render()
    if (this.hasPanelTarget && this.rangeStart && this.rangeEnd && !this.requireApplyButtonValue) {
      this.closePanel()
    }
    if (this.autoSubmitOnCompleteValue && !this.requireApplyButtonValue && this.rangeStart && this.rangeEnd) {
      this.submitForm()
    }
  }

  apply(event) {
    event.preventDefault()
    if (!this.rangeStart) return

    this.ensureRangeCompleteBeforeSubmit()
    this.syncInputs()
    if (this.hasPanelTarget) {
      this.closePanel()
    }
    this.submitForm()
  }

  /** Si solo hay fecha inicial, al enviar el formulario se usa la misma como fin (un día). */
  ensureRangeCompleteBeforeSubmit(_event) {
    if (this.singleDateValue) return
    if (this.rangeStart && !this.rangeEnd) {
      this.rangeEnd = this.rangeStart
      this.syncInputs()
    }
  }

  submitForm() {
    if (!this.formElement || typeof this.formElement.requestSubmit !== "function") return

    this.formElement.requestSubmit()
  }

  syncInputs() {
    if (this.singleDateValue && this.hasSingleInputTarget) {
      const v = this.rangeStart || ""
      this.singleInputTarget.value = v
      this.dispatchInput(this.singleInputTarget)
      this.updateDisplayLabel()
      return
    }

    if (!this.hasStartInputTarget || !this.hasEndInputTarget) return
    this.startInputTarget.value = this.rangeStart || ""
    this.endInputTarget.value = this.rangeEnd || ""
    this.dispatchInput(this.startInputTarget)
    this.dispatchInput(this.endInputTarget)
    this.updateDisplayLabel()
  }

  // Use CustomEvent so domain renames (Event ↔ Lesson) cannot break DOM dispatch.
  dispatchInput (element) {
    element.dispatchEvent(new CustomEvent("input", { bubbles: true }))
  }

  updateDisplayLabel() {
    if (!this.hasDisplayLabelTarget) return
    const start = this.rangeStart

    if (this.singleDateValue) {
      if (!start) {
        this.displayLabelTarget.textContent = ""
        return
      }
      const fmt = this.formatIsoShort(start)
      this.displayLabelTarget.textContent = fmt
      return
    }

    const end = this.rangeEnd || this.rangeStart
    if (!start) {
      this.displayLabelTarget.textContent = ""
      return
    }
    const fmt = (iso) => this.formatIsoShort(iso)
    const endLabel = this.rangeEnd ? fmt(end) : "…"
    this.displayLabelTarget.textContent = `${fmt(start)} → ${endLabel}`
  }

  formatIsoShort(iso) {
    const [y, m, d] = iso.split("-").map(Number)
    const dt = new Date(y, m - 1, d)
    const locale = document.documentElement.lang || "es"
    return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" }).format(dt)
  }

  render() {
    const left = new Date(this.viewMonth)
    const right = new Date(this.viewMonth.getFullYear(), this.viewMonth.getMonth() + 1, 1)

    this.leftTitleTarget.textContent = this.monthTitle(left)
    this.rightTitleTarget.textContent = this.monthTitle(right)

    this.leftMonthTarget.replaceChildren(this.buildMonthGrid(left.getFullYear(), left.getMonth()))
    this.rightMonthTarget.replaceChildren(this.buildMonthGrid(right.getFullYear(), right.getMonth()))
  }

  monthTitle(d) {
    const months = this.translationsValue.months || []
    return `${months[d.getMonth()]} ${d.getFullYear()}`
  }

  buildMonthGrid(year, month) {
    const wrap = document.createElement("div")
    wrap.className = "space-y-2"

    const daysRow = document.createElement("div")
    daysRow.className = "grid grid-cols-7 gap-1 mb-1"
    const dayNames = this.translationsValue.days || []
    dayNames.forEach((day) => {
      const cell = document.createElement("div")
      cell.className = "text-center text-xs font-semibold text-primary-light-color"
      cell.textContent = day
      daysRow.appendChild(cell)
    })
    wrap.appendChild(daysRow)

    const grid = document.createElement("div")
    grid.className = "grid grid-cols-7 gap-1"

    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const startingDay = new Date(year, month, 1).getDay()
    const mondayBasedStart = startingDay === 0 ? 6 : startingDay - 1

    for (let i = 0; i < mondayBasedStart; i++) {
      grid.appendChild(document.createElement("div"))
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`
      grid.appendChild(this.buildDayButton(iso, day))
    }

    wrap.appendChild(grid)
    return wrap
  }

  buildDayButton(iso, dayNum) {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.dataset.iso = iso
    btn.dataset.action = "click->date-range-picker#pickDay"

    const base =
      "h-9 w-full max-w-[2.25rem] mx-auto flex items-center justify-center text-sm font-medium transition-colors border"

    const hasStart = Boolean(this.rangeStart)
    const hasEnd = Boolean(this.rangeEnd)
    const isStart = hasStart && iso === this.rangeStart
    const isEnd = hasEnd && iso === this.rangeEnd
    const singleDay = isStart && isEnd
    const inSpan = hasStart && hasEnd && iso >= this.rangeStart && iso <= this.rangeEnd && !singleDay
    const onlyStartSelected = isStart && !hasEnd
    const onPrimary = singleDay || onlyStartSelected || isStart || isEnd

    const holidayName = this.holidayLabelsValue[iso]
    const isHoliday = holidayName !== undefined && Object.prototype.hasOwnProperty.call(this.holidayLabelsValue, iso)

    let shape = "rounded-full"
    if (inSpan) {
      if (isStart) shape = "rounded-l-full"
      else if (isEnd) shape = "rounded-r-full"
      else shape = "rounded-none"
    }

    let tone = "text-primary-color hover:bg-background-light border-transparent"
    if (singleDay || onlyStartSelected) {
      tone = "bg-button-primary-bg text-button-primary-text border-button-primary-bg"
    } else if (isStart || isEnd) {
      tone = "bg-button-primary-bg text-button-primary-text border-button-primary-bg"
    } else if (inSpan) {
      tone = "bg-button-primary-bg/15 text-primary-color border-transparent"
    }

    if (isHoliday && !onPrimary) {
      tone += " ring-1 ring-inset ring-amber-500/50"
    }

    if (isHoliday) {
      btn.className = `${base} ${shape} ${tone} flex flex-col items-center justify-center gap-0.5 py-0.5 min-h-9 h-auto`.trim()
      const num = document.createElement("span")
      num.className = "text-sm font-semibold leading-none"
      num.textContent = String(dayNum)
      btn.appendChild(num)
      const marker = document.createElement("span")
      marker.setAttribute("aria-hidden", "true")
      marker.className = onPrimary
        ? "h-1.5 w-1.5 shrink-0 rounded-full bg-button-primary-text/85"
        : "h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
      btn.appendChild(marker)
      if (holidayName) {
        btn.title = holidayName
        btn.setAttribute("aria-label", `${holidayName} (${dayNum})`)
      }
    } else {
      btn.className = `${base} ${shape} ${tone}`.trim()
      btn.textContent = String(dayNum)
    }

    btn.setAttribute(
      "aria-pressed",
      singleDay || onlyStartSelected || (inSpan && (isStart || isEnd)) ? "true" : "false"
    )

    return btn
  }

  firstOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth(), 1)
  }

  parseIso(str) {
    if (!str) return null
    const [y, m, day] = str.split("-").map(Number)
    if (!y || !m || !day) return null
    return new Date(y, m - 1, day)
  }

  normalizeIso(str) {
    if (!str || typeof str !== "string") return null
    const m = str.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/)
    return m ? `${m[1]}-${m[2]}-${m[3]}` : null
  }
}
